import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { DATA_TRANSFORM_Y, VITAL_RANGES } from './constants'
import {
  ChartDataPointType,
  ManualDataType,
  ConsciousnessLabelType,
  ConsciousnessUrineValueType,
  UrineLabelType,
} from './types'
import { PatientRecordedDataType } from '../../../../state/recordedData/types'
import { AddsDataType } from '../../../../state/addsData/types'
import { convertISTToLocal } from '../../../../helpers/Timezone'
import { CONSCIOUSNESS_MAPPING, URINE_MAPPING } from './constants'

dayjs.extend(utc)
dayjs.extend(timezone)

export const getLatestPoints = (data: AddsDataType[]): ChartDataPointType[] => {
  let chartDataPoints: ChartDataPointType[] = []
  const sortedData = [...data].sort(
    (a, b) => b.createTimestampSeconds! - a.createTimestampSeconds!
  )
  const foundVitals = new Set<string>()
  const vitalArray = [
    'resp',
    'spo2',
    'pr',
    'temp',
    'dia',
    'sys',
    'urine',
    'consc',
    'pain',
  ]

  for (const dataPoint of sortedData) {
    let count = 0
    let chartDataPoint = createBlankChartDataPoint(dataPoint.createTimestamp)

    for (const vital of vitalArray) {
      if (foundVitals.has(vital)) {
        continue
      }
      if (vital === 'resp' && dataPoint.resp) {
        chartDataPoint.respiration = dataPoint.resp
        chartDataPoint.resp = getVitalValue('RESPIRATORY_RATE', dataPoint.resp)
        foundVitals.add(vital)
        count++
      }
      if (vital === 'spo2' && dataPoint.spo2) {
        chartDataPoint.o2Saturation = dataPoint.spo2
        chartDataPoint.spo2 = getVitalValue('O2_SATURATION', dataPoint.spo2)
        foundVitals.add(vital)
        count++
      }
      if (vital === 'pr' && dataPoint.pr) {
        chartDataPoint.heartRate = dataPoint.pr
        chartDataPoint.pr = getVitalValue('PULSE_RATE', dataPoint.pr)
        foundVitals.add(vital)
        count++
      }
      if (vital === 'temp' && dataPoint.temp) {
        chartDataPoint.temperature = dataPoint.temp
        chartDataPoint.temp = getVitalValue('TEMPERATURE', dataPoint.temp)
        foundVitals.add(vital)
        count++
      }
      if (vital === 'dia' && dataPoint.dia) {
        chartDataPoint.dia = dataPoint.dia
        foundVitals.add(vital)
        count++
      }
      if (vital === 'sys' && dataPoint.sys) {
        chartDataPoint.sys = dataPoint.sys
        foundVitals.add(vital)
        count++
      }

      if (vital === 'urine' && dataPoint.urine) {
        chartDataPoint.fourHourUrineOutput = dataPoint.urine
        chartDataPoint.urine = getUrineValue(dataPoint.urine)
        foundVitals.add(vital)
        count++
      }
      if (vital === 'consc' && dataPoint.consc) {
        chartDataPoint.consciousness = dataPoint.consc
        chartDataPoint.consc = getConsciousnessValue(dataPoint.consc)
        foundVitals.add(vital)
        count++
      }
      if (vital === 'pain' && (dataPoint.pain || dataPoint.pain === 0)) {
        chartDataPoint.painValue = dataPoint.pain
        chartDataPoint.pain = getVitalValue('PAIN', dataPoint.pain)
        foundVitals.add(vital)
        count++
      }
    }
    if (count > 0) {
      chartDataPoints.push(chartDataPoint)
    }
    if (vitalArray.length === foundVitals.size) {
      break
    }
  }
  return chartDataPoints
}

export const aggregateData = (
  data: AddsDataType[],
  timeStamp: Dayjs,
  mode: 'AVERAGE' | 'LATEST'
): ChartDataPointType | undefined => {
  let respValues: number[] = []
  let spo2Values: number[] = []
  let prValues: number[] = []
  let tempValues: number[] = []
  let diaValues: number[] = []
  let sysValues: number[] = []
  let urineValues: string[] = []
  let conscValues: string[] = []
  let painValues: number[] = []

  data.forEach((dataObject) => {
    if (dataObject.resp) {
      respValues.push(dataObject.resp)
    }
    if (dataObject.spo2) {
      spo2Values.push(dataObject.spo2)
    }
    if (dataObject.pr) {
      prValues.push(dataObject.pr)
    }
    if (dataObject.temp) {
      tempValues.push(dataObject.temp)
    }
    if (dataObject.dia) {
      diaValues.push(dataObject.dia)
    }
    if (dataObject.sys) {
      sysValues.push(dataObject.sys)
    }
    if (dataObject.urine) {
      urineValues.push(dataObject.urine)
    }
    if (dataObject.consc) {
      conscValues.push(dataObject.consc)
    }
    if (dataObject.pain || dataObject.pain === 0) {
      painValues.push(dataObject.pain)
    }
  })

  const respiration =
    mode === 'AVERAGE' ? getAverage(respValues) : Number(getLatest(respValues))
  const o2Saturation =
    mode === 'AVERAGE' ? getAverage(spo2Values) : Number(getLatest(spo2Values))
  const heartRate =
    mode === 'AVERAGE' ? getAverage(prValues) : Number(getLatest(prValues))
  const temperature =
    mode === 'AVERAGE' ? getAverage(tempValues) : Number(getLatest(tempValues))
  const dia = getAverage(diaValues)
  const sys = getAverage(sysValues)
  const fourHourUrineOutput = String(getLatest(urineValues))
  const consciousness = String(getLatest(conscValues))
  const painValue =
    mode === 'AVERAGE' ? getAverage(painValues) : Number(getLatest(painValues))

  return {
    createTimestamp: timeStamp.toDate(),
    createTimestampSeconds: timeStamp.unix(),
    respiration: respiration,
    resp: getVitalValue('RESPIRATORY_RATE', respiration),
    o2Saturation: o2Saturation,
    spo2: getVitalValue('O2_SATURATION', o2Saturation),
    heartRate: heartRate,
    pr: getVitalValue('PULSE_RATE', heartRate),
    temperature: temperature,
    temp: getVitalValue('TEMPERATURE', temperature),
    dia: dia,
    sys: sys,
    fourHourUrineOutput: fourHourUrineOutput,
    urine: getUrineValue(fourHourUrineOutput),
    consciousness: consciousness,
    consc: getConsciousnessValue(consciousness),
    painValue: painValue,
    pain: getVitalValue('PAIN', painValue),
  }
}

export const getVitalValue = (
  vitalType: keyof typeof VITAL_RANGES,
  value: number
): number | null => {
  const vitalRange = VITAL_RANGES[vitalType]
  type RangeKeys = Exclude<keyof typeof vitalRange, 'RANGES'>
  const ranges = [...vitalRange.RANGES] as RangeKeys[]
  for (const range of ranges) {
    if (value >= vitalRange[range].min) {
      return vitalRange[range].chart_value + DATA_TRANSFORM_Y
    }
  }
  return null
}

export const getConsciousnessValue = (label: string): number | null => {
  const value = CONSCIOUSNESS_MAPPING.labels[label as ConsciousnessLabelType]
  return value ? value + DATA_TRANSFORM_Y : null
}

export const getConsciousnessLabel = (value: number): string =>
  CONSCIOUSNESS_MAPPING.values[value as ConsciousnessUrineValueType] ?? ''

export const getUrineValue = (label: string): number | null => {
  const value = URINE_MAPPING.labels[label as UrineLabelType]
  return value ? value + DATA_TRANSFORM_Y : null
}

export const getUrineLabel = (value: number): string =>
  URINE_MAPPING.values[value as ConsciousnessUrineValueType] ?? ''

export const getBandedRanges = (vitalType: keyof typeof VITAL_RANGES) =>
  Object.entries(VITAL_RANGES[vitalType]).map(([key, value]) => ({
    key,
    min: value.banded_min,
    max: value.banded_max,
    color: value.color,
  }))

export const createTimeArray = (
  startTimeUnix: number,
  endTimeUnix: number,
  intervalMins: number
): Dayjs[] => {
  const endTime = dayjs(endTimeUnix)
  let time: Dayjs = dayjs(startTimeUnix)
  const timeArray: Dayjs[] = []
  do {
    timeArray.push(time)
    time = time.add(intervalMins, 'minutes')
  } while (time <= endTime)
  return timeArray
}

export const createTimeObjectArray = (timeArray: Dayjs[]) => {
  return timeArray.map((time) => ({
    createTimestampSeconds: time.unix(),
    createTimestamp: time,
  }))
}

export const setRecordedToAdds = (
  data: PatientRecordedDataType
): AddsDataType => {
  // const createTime = dayjs(convertISTToLocal(data.createTimestamp))
  // const time = dayjs(data.createTimestamp, 'DD-MM-YYYY hh:mm:ss.SSS')
  // let createTime = dayjs.tz(time, "UTC").tz(dayjs.tz.guess());

  // const time = dayjs('2025-02-23 10:35:00') // your timestamp in UTC
  // const createTime = time.tz(dayjs.tz.guess()) // converts to local timezone
  // const localTimeKeepTime = time.tz(dayjs.tz.guess(), true)

  // const time = dayjs.utc(data.createTimestamp, 'DD-MM-YYYY HH:mm:ss.SSS')
  // const time = dayjs.utc(data.createTimestamp, 'YYYY-MM-DD HH:mm:ss')
  const time = dayjs.utc(data.createTimestamp)
  const createTime = time.local()


  return {
    createTimestamp: createTime.toDate(),
    createTimestampSeconds: createTime.unix(),
    resp: data.resp,
    spo2: data.spo2,
    pr: data.pr,
    temp: fahrenheitToCelsius(data.temp),
    dia: data.dia,
    sys: data.sys,
    urine: null,
    consc: null,
    pain: null,
  }
}

export const setManualToAdds = (data: ManualDataType): AddsDataType[] => {
  const createTime = dayjs(data.createTimestamp)
  let result: AddsDataType[] = []

  if (data.fourHourUrineOutput) {
    const urineTime = dayjs(data.urineOutputTimestamp)
    result.push({
      createTimestamp: urineTime.toDate(),
      createTimestampSeconds: urineTime.unix(),
      resp: null,
      spo2: null,
      pr: null,
      temp: null,
      dia: null,
      sys: null,
      urine: data.fourHourUrineOutput,
      consc: null,
      pain: null,
    })
  }
  result.push({
    createTimestamp: createTime.toDate(),
    createTimestampSeconds: createTime.unix(),
    resp: null,
    spo2: null,
    pr: null,
    temp: null,
    dia: null,
    sys: null,
    urine: null,
    consc: data.consciousness,
    pain: data.painLevel,
  })

  return result
}

export const getMillisecondsFromMinutes = (minutes: number): number => {
  return minutes * 60 * 1000
}

export const getMillisecondsFromHour = (hours: number): number => {
  return hours * 60 * 60 * 1000
}

export const isBetweenDayjs = (
  time: Dayjs,
  lowerTime: Dayjs,
  upperTime: Dayjs
): boolean => {
  if (
    (time.isSame(lowerTime) || time.isAfter(lowerTime)) &&
    time.isBefore(upperTime)
  ) {
    return true
  }
  return false
}

export const fahrenheitToCelsius = (
  fahrenheit: number | null
): number | null => {
  return fahrenheit ? (fahrenheit - 32) * (5 / 9) : null
}

export const getAverage = (vitalValues: number[]): number => {
  const sum = vitalValues.reduce((a, b) => a + b, 0)
  return sum / vitalValues.length
}

export const getLatest = (
  vitalValues: string[] | number[]
): number | string => {
  const length = vitalValues.length
  return vitalValues[length - 1]
}

export const datetimeToAusFormat = (d: Date): string => {
  return d.toLocaleString('en-AU', {
    hour12: false,
  })
}

export const dateToAusFormat = (d: string): string => {
  return new Date(d).toLocaleDateString('en-AU')
}

export const createChartData = (
  timeArray: Dayjs[],
  intervalMinutes: number,
  addsData: AddsDataType[]
) => {
  const chartData: ChartDataPointType[] = []

  let index: number = 0
  for (const baseTime of timeArray) {
    const chartIntervalData: AddsDataType[] = []
    const startIntervalTime = baseTime.unix()
    const endIntervalTime = baseTime.add(intervalMinutes, 'minute').unix()
    while (index < addsData.length) {
      let timeSeconds = addsData[index].createTimestampSeconds!
      if (timeSeconds < startIntervalTime) {
        index++
      } else if (
        timeSeconds >= startIntervalTime &&
        timeSeconds < endIntervalTime
      ) {
        chartIntervalData.push(addsData[index])
        index++
      } else {
        break
      }
    }

    if (chartIntervalData.length > 0) {
      // chartData.push(aggregateData(chartIntervalData, baseTime, 'LATEST')!)
      chartData.push(...getLatestPoints(chartIntervalData))
    }
  }
  return chartData.sort(
    (a, b) => a.createTimestampSeconds! - b.createTimestampSeconds!
  )
}

export const createBlankChartDataPoint = (
  createTimestamp: Date
): ChartDataPointType => {
  return {
    createTimestamp: createTimestamp,
    createTimestampSeconds: createTimestamp.valueOf() / 1000,
    respiration: null,
    resp: null,
    o2Saturation: null,
    spo2: null,
    heartRate: null,
    pr: null,
    temperature: null,
    temp: null,
    dia: null,
    sys: null,
    fourHourUrineOutput: null,
    urine: null,
    consciousness: null,
    consc: null,
    painValue: null,
    pain: null,
  }
}

export const generateTimeTicks = (
  timeArray: Dayjs[],
  duration: number,
  interval: number
): number[] => {
  const numberOfValues = (60 / interval) * duration
  let numberOfTicks = numberOfValues
  if (numberOfValues === 36) {
    numberOfTicks = 18
  } else if (numberOfValues > 24) {
    numberOfTicks = 24
  }

  const tickInterval = numberOfValues / numberOfTicks

  let ticks: number[] = []
  for (let i = 0; i <= numberOfValues; i += tickInterval) {
    ticks.push(timeArray[i].unix())
  }

  return ticks
}
